/**
 * Catálogo das tags públicas do profissional (espelha os enums do backend):
 * tag de destaque (uma só), abordagens da psicologia e temas de atuação.
 * Usado nos cards, no perfil do profissional, no cadastro e no admin.
 */

export type ProfessionalHighlightTag =
	| 'ACESSIVEL'
	| 'BOA_ORGANIZACAO'
	| 'FOCO_EM_METAS'
	| 'ACOLHIMENTO'
	| 'ESCUTA_ATIVA'
	| 'ATENDE_JOVENS'
	| 'ATENDE_FAMILIAS_E_CASAIS'
	| 'PRIMEIRA_TERAPIA';

export type PsychologyApproach =
	| 'TCC'
	| 'PSICANALISE'
	| 'JUNGUIANA'
	| 'HUMANISTA'
	| 'GESTALT'
	| 'SISTEMICA'
	| 'ACT'
	| 'ESQUEMA'
	| 'DBT'
	| 'EXISTENCIAL'
	| 'ANALISE_DO_COMPORTAMENTO'
	| 'PSICODRAMA'
	| 'EMDR'
	| 'INTEGRATIVA';

export type ProfessionalFocusTopic =
	| 'ANSIEDADE'
	| 'DEPRESSAO'
	| 'RELACIONAMENTOS'
	| 'LUTO'
	| 'AUTOESTIMA'
	| 'CARREIRA_E_BURNOUT'
	| 'TDAH'
	| 'TRAUMA'
	| 'FAMILIA_E_PARENTALIDADE'
	| 'TRANSTORNOS_ALIMENTARES'
	| 'DEPENDENCIA'
	| 'NEURODIVERGENCIA'
	| 'ADOLESCENCIA'
	| 'TERCEIRA_IDADE';

export interface HighlightTagMeta {
	key: ProfessionalHighlightTag;
	label: string;
	/** Frase curta mostrada no seletor do perfil e no tooltip do card. */
	hint: string;
	/** Classes Tailwind do chip (fundo + texto). */
	chipClassName: string;
	/** Nome de ícone lucide (usado com DynamicIcon ou mapa local). */
	icon:
		| 'sparkles'
		| 'calendar-check'
		| 'target'
		| 'heart-handshake'
		| 'ear'
		| 'users'
		| 'home'
		| 'sprout';
}

export const HIGHLIGHT_TAGS: HighlightTagMeta[] = [
	{
		key: 'ACESSIVEL',
		label: 'Acessível',
		hint: 'Linguagem simples, sem jargão, no seu ritmo',
		chipClassName: 'bg-violet-100 text-violet-800',
		icon: 'sparkles',
	},
	{
		key: 'BOA_ORGANIZACAO',
		label: 'Boa organização',
		hint: 'Pontualidade e continuidade entre as sessões',
		chipClassName: 'bg-sky-100 text-sky-800',
		icon: 'calendar-check',
	},
	{
		key: 'FOCO_EM_METAS',
		label: 'Foco em metas',
		hint: 'Objetivos claros e acompanhamento do progresso',
		chipClassName: 'bg-amber-100 text-amber-800',
		icon: 'target',
	},
	{
		key: 'ACOLHIMENTO',
		label: 'Acolhimento',
		hint: 'Espaço seguro e sem julgamentos',
		chipClassName: 'bg-rose-100 text-rose-800',
		icon: 'heart-handshake',
	},
	{
		key: 'ESCUTA_ATIVA',
		label: 'Escuta ativa',
		hint: 'Mais escuta, menos pressa',
		chipClassName: 'bg-teal-100 text-teal-800',
		icon: 'ear',
	},
	{
		key: 'ATENDE_JOVENS',
		label: 'Atende jovens',
		hint: 'Experiência com adolescentes e universitários',
		chipClassName: 'bg-lime-100 text-lime-800',
		icon: 'users',
	},
	{
		key: 'ATENDE_FAMILIAS_E_CASAIS',
		label: 'Famílias e casais',
		hint: 'Atendimento de casal e família',
		chipClassName: 'bg-orange-100 text-orange-800',
		icon: 'home',
	},
	{
		key: 'PRIMEIRA_TERAPIA',
		label: 'Primeira terapia',
		hint: 'Bom para quem nunca fez terapia',
		chipClassName: 'bg-emerald-100 text-emerald-800',
		icon: 'sprout',
	},
];

export interface ApproachMeta {
	key: PsychologyApproach;
	label: string;
	/** Nome curto para o card compacto. */
	short: string;
	/** Uma linha explicando a abordagem para o paciente leigo. */
	hint: string;
}

export const APPROACHES: ApproachMeta[] = [
	{
		key: 'TCC',
		label: 'Terapia Cognitivo-Comportamental (TCC)',
		short: 'TCC',
		hint: 'Trabalha pensamentos e comportamentos com metas práticas',
	},
	{
		key: 'PSICANALISE',
		label: 'Psicanálise',
		short: 'Psicanálise',
		hint: 'Explora o inconsciente e a história de vida',
	},
	{
		key: 'JUNGUIANA',
		label: 'Psicologia Analítica (Junguiana)',
		short: 'Junguiana',
		hint: 'Símbolos, sonhos e processo de individuação',
	},
	{
		key: 'HUMANISTA',
		label: 'Humanista / Centrada na Pessoa',
		short: 'Humanista',
		hint: 'Escuta empática e autonomia da pessoa',
	},
	{
		key: 'GESTALT',
		label: 'Gestalt-terapia',
		short: 'Gestalt',
		hint: 'Consciência do aqui e agora e do contato',
	},
	{
		key: 'SISTEMICA',
		label: 'Terapia Sistêmica',
		short: 'Sistêmica',
		hint: 'Relações e sistemas familiares',
	},
	{
		key: 'ACT',
		label: 'Terapia de Aceitação e Compromisso (ACT)',
		short: 'ACT',
		hint: 'Aceitação, valores e ação comprometida',
	},
	{
		key: 'ESQUEMA',
		label: 'Terapia do Esquema',
		short: 'Esquema',
		hint: 'Padrões antigos que se repetem na vida adulta',
	},
	{
		key: 'DBT',
		label: 'Terapia Comportamental Dialética (DBT)',
		short: 'DBT',
		hint: 'Regulação emocional e habilidades práticas',
	},
	{
		key: 'EXISTENCIAL',
		label: 'Fenomenológico-Existencial',
		short: 'Existencial',
		hint: 'Sentido, liberdade e escolhas',
	},
	{
		key: 'ANALISE_DO_COMPORTAMENTO',
		label: 'Análise do Comportamento',
		short: 'Análise do Comportamento',
		hint: 'Contexto e consequências do comportamento',
	},
	{
		key: 'PSICODRAMA',
		label: 'Psicodrama',
		short: 'Psicodrama',
		hint: 'Dramatização e espontaneidade',
	},
	{
		key: 'EMDR',
		label: 'EMDR',
		short: 'EMDR',
		hint: 'Reprocessamento de memórias traumáticas',
	},
	{
		key: 'INTEGRATIVA',
		label: 'Integrativa',
		short: 'Integrativa',
		hint: 'Combina técnicas de mais de uma abordagem',
	},
];

export interface FocusTopicMeta {
	key: ProfessionalFocusTopic;
	label: string;
}

export const FOCUS_TOPICS: FocusTopicMeta[] = [
	{ key: 'ANSIEDADE', label: 'Ansiedade' },
	{ key: 'DEPRESSAO', label: 'Depressão' },
	{ key: 'RELACIONAMENTOS', label: 'Relacionamentos' },
	{ key: 'LUTO', label: 'Luto' },
	{ key: 'AUTOESTIMA', label: 'Autoestima' },
	{ key: 'CARREIRA_E_BURNOUT', label: 'Carreira e burnout' },
	{ key: 'TDAH', label: 'TDAH' },
	{ key: 'TRAUMA', label: 'Trauma' },
	{ key: 'FAMILIA_E_PARENTALIDADE', label: 'Família e parentalidade' },
	{ key: 'TRANSTORNOS_ALIMENTARES', label: 'Transtornos alimentares' },
	{ key: 'DEPENDENCIA', label: 'Dependência' },
	{ key: 'NEURODIVERGENCIA', label: 'Neurodivergência' },
	{ key: 'ADOLESCENCIA', label: 'Adolescência' },
	{ key: 'TERCEIRA_IDADE', label: 'Terceira idade' },
];

export const MAX_SECONDARY_APPROACHES = 2;
export const MAX_FOCUS_TOPICS = 4;
/** A partir de quantas sessões concluídas os números aparecem no card expandido. */
export const SHOWCASE_MIN_SESSIONS_TO_DISPLAY = 50;

export const getHighlightTag = (key?: string | null) =>
	HIGHLIGHT_TAGS.find((t) => t.key === key) ?? null;

export const getApproach = (key?: string | null) =>
	APPROACHES.find((a) => a.key === key) ?? null;

export const getFocusTopic = (key?: string | null) =>
	FOCUS_TOPICS.find((t) => t.key === key) ?? null;

/** Serviço de psicologia? (mesma regra do backend: contém "psicolog" sem acento) */
export const isPsychologyServiceName = (
	...names: Array<string | null | undefined>
) =>
	names
		.filter(Boolean)
		.join(' ')
		.toLocaleLowerCase('pt-BR')
		.normalize('NFD')
		.replace(/[̀-ͯ]/g, '')
		.includes('psicolog');

export type ProfessionalGender = 'FEMININO' | 'MASCULINO' | 'PREFIRO_NAO_INFORMAR';

export const GENDERS: Array<{ key: ProfessionalGender; label: string }> = [
	{ key: 'PREFIRO_NAO_INFORMAR', label: 'Prefiro não informar' },
	{ key: 'FEMININO', label: 'Feminino' },
	{ key: 'MASCULINO', label: 'Masculino' },
];

export const getGenderLabel = (key?: string | null) =>
	GENDERS.find((g) => g.key === key)?.label ?? 'Prefiro não informar';

/** Códigos das questões do match, para o admin entender o que cada uma alimenta. */
export const MATCH_QUESTION_CODE_LABELS: Record<string, string> = {
	TOPICS: 'Temas de atuação',
	STYLE: 'Abordagem (estilo)',
	PRIORITY: 'Tag de destaque',
	EXPERIENCE: 'Primeira terapia',
	GENDER_PREF: 'Gênero do profissional',
	AUDIENCE: 'Público (casal/família)',
	AVAILABILITY: 'Período da agenda',
	AGE: 'Faixa etária do paciente',
};
